import React, { useState } from 'react';

import { DeleteAccountModal } from '@/components/DeleteAccountModal';
import { usePurgeAccount } from '@/hooks/useAccounts';
import { AISpendCard } from '@/sections/settings/components/cards/AISpendCard';
import { AllowedDomainsSection } from '@/sections/settings/components/cards/AllowedDomainsSection';
import { OrgProfileSection } from '@/sections/settings/components/cards/OrgProfileSection';
import { PraetorianFeaturesSection } from '@/sections/settings/components/cards/PraetorianFeaturesSection';
import { SSOProviderSection } from '@/sections/settings/components/cards/SSOProviderSection';
import {
  aiModuleOptions,
  staticIpModuleOptions,
} from '@/sections/settings/components/sections/chariotModuleOptions';
import { useAuth } from '@/state/auth';

interface OrganizationSettingsCardsProps {
  email?: string;
  editable?: boolean;
}

/**
 * Unified organization settings component grouping profile, SSO, and Praetorian
 * features into a single cohesive interface.
 *
 * Architecture:
 * - Orchestrator component that composes 3 specialized sections
 * - Minimal logic, delegates to child components
 * - Handles only delete account modal (cross-cutting concern)
 *
 * Child Components:
 * - OrgProfileSection: Profile picture and organization name
 * - SSOProviderSection: SSO provider management with add/remove
 * - PraetorianFeaturesSection: Feature flags and account deletion (Praetorian only)
 *
 * Features:
 * - Impersonation support via email prop
 * - Conditional Praetorian features based on user role
 * - Consistent gap-2 spacing matching ScanSettingsCards pattern
 *
 * React 19 Features:
 * - useOptimistic: Delegated to PraetorianFeaturesSection
 * - Standard state: Delete modal visibility
 *
 * Used in:
 * - OrganizationTab
 */
export const OrganizationSettingsCards: React.FC<
  OrganizationSettingsCardsProps
> = ({ email, editable = true }) => {
  const { me, isPraetorianUser } = useAuth();

  // Account deletion (Praetorian only)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { mutateAsync: purgeAccount, status: purgeAccountStatus } =
    usePurgeAccount();

  const handleDeleteAccount = async () => {
    await purgeAccount();
    setIsDeleteModalOpen(false);
  };

  const showPraetorianFeatures =
    isPraetorianUser || me === 'admin@praetorian.com';

  return (
    <>
      <div className="flex flex-col gap-2">
        {/* Organization Profile Section */}
        <OrgProfileSection email={email} editable={editable} />

        {/* SSO Provider Section */}
        <SSOProviderSection email={email} editable={editable} />

        {/* Allowed Email Domains Section */}
        {showPraetorianFeatures && (
          <AllowedDomainsSection email={email} editable={editable} />
        )}

        {/* Static IP Addresses (standalone)
            Always rendered so customers can SEE the toggle (read-only),
            directly below Allowed Email Domains. Praetorian users may
            manage it; customers see it disabled with a "Managed by
            Praetorian" tooltip reflecting its current state. */}
        <PraetorianFeaturesSection
          email={email}
          editable={editable}
          options={staticIpModuleOptions}
          canToggleFlags={showPraetorianFeatures}
          showDeleteAccount={false}
        />

        {/* AI Features Section
            Always rendered so customers can SEE the AI feature toggles
            (read-only). Praetorian users may manage the flags; customers
            see each toggle disabled with a "Managed by Praetorian"
            tooltip reflecting its current state. The Delete Account
            danger card stays Praetorian-only via showDeleteAccount.

            AI Spend is slotted as the first card under the "AI Features"
            header (visible to all users; the card renders the marked-up
            budget/spend values verbatim from the GET hook, with editing
            gated to Praetorians inside AISpendCard). */}
        <PraetorianFeaturesSection
          email={email}
          editable={editable}
          options={aiModuleOptions}
          header="AI Features"
          cardsAfterHeader={<AISpendCard email={email} />}
          canToggleFlags={showPraetorianFeatures}
          showDeleteAccount={showPraetorianFeatures}
          onDeleteAccount={() => setIsDeleteModalOpen(true)}
        />
      </div>

      {/* Delete Account Modal */}
      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteAccount}
        accountName={email || me}
        isPending={purgeAccountStatus === 'pending'}
      />
    </>
  );
};
